import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DollarSign, Download, Edit, Eye, CheckCircle, ArrowLeft, Plus, Minus, AlertCircle, Link2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChargeSheet({ game, onClose, user }) {
  const [players, setPlayers] = useState([]);
  const [totalBill, setTotalBill] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [hasSubsidy, setHasSubsidy] = useState(false);
  const [manualSubsidyMode, setManualSubsidyMode] = useState(false);
  const [manualSubsidyAmount, setManualSubsidyAmount] = useState("");
  const [chargeMethod, setChargeMethod] = useState("multiplier");
  const [fixedAmount, setFixedAmount] = useState("");
  const [remarksToFinance, setRemarksToFinance] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [subsidyAmount, setSubsidyAmount] = useState(0);
  
  // New states for edit mode and preview
  const [existingSheet, setExistingSheet] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [calculatedCharges, setCalculatedCharges] = useState([]);
  
  // Link member dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingPlayer, setLinkingPlayer] = useState(null);
  const [linkMembershipNumber, setLinkMembershipNumber] = useState("");
  const [linkingInProgress, setLinkingInProgress] = useState(false);

  useEffect(() => {
    checkExistingSheet();
  }, []);

  useEffect(() => {
    calculateSubsidy();
  }, [totalBill, hasSubsidy, manualSubsidyMode, manualSubsidyAmount]);

  const checkExistingSheet = async () => {
    setLoading(true);
    try {
      // Check if charge sheet already exists
      const existsResponse = await axios.get(
        `${API}/games/${game.id}/charge-sheet/exists`,
        { withCredentials: true }
      );

      if (existsResponse.data.exists) {
        setExistingSheet(existsResponse.data.charge_sheet);
        // Load existing sheet data
        await loadExistingSheet();
      } else {
        // No existing sheet, load players for new sheet
        await loadPlayers();
      }
    } catch (error) {
      console.error("Error checking existing sheet:", error);
      await loadPlayers();
    } finally {
      setLoading(false);
    }
  };

  const loadExistingSheet = async () => {
    try {
      const response = await axios.get(
        `${API}/games/${game.id}/charge-sheet`,
        { withCredentials: true }
      );

      const sheet = response.data;
      
      // Populate form with existing data
      setTotalBill(sheet.total_bill.toString());
      setReceiptNumber(sheet.receipt_number || "");
      setHasSubsidy(sheet.has_subsidy);
      setChargeMethod(sheet.charge_method);
      if (sheet.fixed_amount) {
        setFixedAmount(sheet.fixed_amount.toString());
      }
      if (sheet.remarks_to_finance) {
        setRemarksToFinance(sheet.remarks_to_finance);
      }
      
      // Check if manual subsidy was used (compare with auto-calculated)
      if (sheet.has_subsidy && sheet.subsidy_amount !== undefined) {
        const autoSubsidy = sheet.total_bill >= 75 ? Math.min(sheet.total_bill * 0.5, 100) : 0;
        if (Math.abs(sheet.subsidy_amount - autoSubsidy) > 0.01) {
          // Manual subsidy was used
          setManualSubsidyMode(true);
          setManualSubsidyAmount(sheet.subsidy_amount.toString());
        }
        setSubsidyAmount(sheet.subsidy_amount);
      }

      // Load players from sheet
      const playersList = sheet.players.map(p => ({
        user_id: p.user_id,
        name: p.name,
        membership_number: p.membership_number,
        multiplier: p.multiplier,
        included: p.included,
        fixedAmount: p.charge_amount?.toString() || "",
        signupType: "playing"
      }));

      setPlayers(playersList);
      setIsEditMode(true);
    } catch (error) {
      console.error("Error loading existing sheet:", error);
      toast.error("Failed to load existing charge sheet");
    }
  };

  const loadPlayers = async () => {
    try {
      // PRIORITY: Use attendance if taken, otherwise use signups
      let playerSource = [];
      let useAttendance = false;
      
      // Check if attendance has been taken
      try {
        const attendanceRes = await axios.get(`${API}/games/${game.id}/attendance`, { withCredentials: true });
        if (attendanceRes.data.attendance_taken && attendanceRes.data.attendance?.length > 0) {
          // Use attendance - only include present and late_addition
          playerSource = attendanceRes.data.attendance.filter(a => a.status !== 'absent');
          useAttendance = true;
        }
      } catch (e) {
        console.log("Attendance not available, using signups");
      }
      
      // Fall back to signups if no attendance
      if (!useAttendance) {
        playerSource = game.signups || [];
      }
      
      const userPromises = playerSource.map(async (record) => {
        // Handle both regular users (user_id) and guest players (guest_id)
        const userId = typeof record === 'string' ? record : (record.user_id || record.guest_id);
        const signupType = typeof record === 'string' ? 'playing' : (record.signup_type || 'playing');
        // Guests store name directly in 'name', regular users use 'user_name'
        const storedName = typeof record === 'object' ? (record.user_name || record.name) : "Unknown";
        const isGuestPlayer = typeof record === 'object' && record.is_guest;
        const attendanceStatus = record.status || null;
        
        let userName = storedName;
        let membershipNumber = record.membership_number || "N/A";
        let memberType = isGuestPlayer ? "guest" : "member";
        
        // For guest players, use the data from the signup directly - don't try to look them up
        if (isGuestPlayer) {
          return {
            user_id: userId,
            name: userName,
            membership_number: membershipNumber,
            member_type: "guest",
            multiplier: 1,
            included: signupType === "playing",
            fixedAmount: "",
            signupType: signupType,
            attendanceStatus: attendanceStatus,
            is_guest: true
          };
        }
        
        // If we already have membership number from attendance, use it
        if (membershipNumber && membershipNumber !== "N/A") {
          return {
            user_id: userId,
            name: userName,
            membership_number: membershipNumber,
            member_type: memberType,
            multiplier: 1,
            included: signupType === "playing",
            fixedAmount: "",
            signupType: signupType,
            attendanceStatus: attendanceStatus
          };
        }
        
        // PRIMARY STRATEGY: Use Member Tracker (unified lookup)
        try {
          const trackerUrl = `${API}/member-tracker/lookup?user_id=${encodeURIComponent(userId)}&name=${encodeURIComponent(storedName)}`;
          const trackerResponse = await axios.get(trackerUrl, { withCredentials: true });
          
          if (trackerResponse.data && trackerResponse.data.found) {
            userName = trackerResponse.data.canonical_name || storedName;
            membershipNumber = trackerResponse.data.membership_number || "N/A";
            memberType = trackerResponse.data.member_type || "member";
          } else {
            memberType = trackerResponse.data.member_type || "member";
            
            try {
              const lookupUrl = `${API}/lookup/player-membership/${userId}?name=${encodeURIComponent(storedName)}`;
              const lookupResponse = await axios.get(lookupUrl, { withCredentials: true });
              
              if (lookupResponse.data) {
                userName = lookupResponse.data.name || storedName;
                membershipNumber = lookupResponse.data.membership_number || "N/A";
              }
            } catch (e) {
              console.log(`Fallback lookup failed for ${storedName}`);
            }
          }
        } catch (e1) {
          console.log(`Member tracker lookup failed for ${storedName}, trying fallback`);
          
          try {
            const lookupUrl = `${API}/lookup/player-membership/${userId}?name=${encodeURIComponent(storedName)}`;
            const lookupResponse = await axios.get(lookupUrl, { withCredentials: true });
            
            if (lookupResponse.data) {
              userName = lookupResponse.data.name || storedName;
              membershipNumber = lookupResponse.data.membership_number || "N/A";
            }
          } catch (e2) {
            console.log(`All lookups failed for ${storedName} (${userId})`);
          }
        }
        
        return {
          user_id: userId,
          name: userName,
          membership_number: membershipNumber,
          member_type: memberType,
          multiplier: 1,
          included: signupType === "playing",
          fixedAmount: "",
          signupType: signupType,
          attendanceStatus: attendanceStatus
        };
      });

      const loadedPlayers = await Promise.all(userPromises);
      setPlayers(loadedPlayers);
    } catch (error) {
      console.error("Error loading players:", error);
      toast.error("Failed to load players");
    }
  };

  const calculateSubsidy = () => {
    if (!hasSubsidy) {
      setSubsidyAmount(0);
      return;
    }
    
    if (manualSubsidyMode) {
      // Use manual subsidy amount
      const manual = parseFloat(manualSubsidyAmount) || 0;
      setSubsidyAmount(manual);
    } else {
      // Auto-calculate subsidy
      const bill = parseFloat(totalBill) || 0;
      if (bill >= 75) {
        const subsidy = Math.min(bill * 0.5, 100);
        setSubsidyAmount(subsidy);
      } else {
        setSubsidyAmount(0);
      }
    }
  };

  const togglePlayerIncluded = (index) => {
    const updated = [...players];
    updated[index].included = !updated[index].included;
    setPlayers(updated);
  };

  const updatePlayerMultiplier = (index, value) => {
    const updated = [...players];
    updated[index].multiplier = parseInt(value) || 1;
    setPlayers(updated);
  };

  const updatePlayerFixedAmount = (index, value) => {
    const updated = [...players];
    updated[index].fixedAmount = value;
    setPlayers(updated);
  };

  const openLinkDialog = (player, index) => {
    setLinkingPlayer({ ...player, index });
    setLinkMembershipNumber("");
    setLinkDialogOpen(true);
  };

  const handleLinkMember = async () => {
    if (!linkMembershipNumber.trim()) {
      toast.error("Please enter a membership number");
      return;
    }

    setLinkingInProgress(true);
    try {
      const response = await axios.post(
        `${API}/member-tracker/link`,
        {
          user_id: linkingPlayer.user_id,
          membership_number: linkMembershipNumber.trim(),
          name_variation: linkingPlayer.name
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(`Linked ${linkingPlayer.name} to ${linkMembershipNumber.toUpperCase()}`);
        
        // Update the player in the list
        const updated = [...players];
        updated[linkingPlayer.index].membership_number = linkMembershipNumber.toUpperCase();
        setPlayers(updated);
        
        setLinkDialogOpen(false);
      }
    } catch (error) {
      console.error("Error linking member:", error);
      toast.error(error.response?.data?.detail || "Failed to link member");
    } finally {
      setLinkingInProgress(false);
    }
  };

  const calculatePreview = () => {
    const bill = parseFloat(totalBill) || 0;
    const netAmount = bill - subsidyAmount;
    const includedPlayers = players.filter(p => p.included);

    if (chargeMethod === "fixed") {
      return includedPlayers.map(p => ({ 
        ...p, 
        charge: parseFloat(p.fixedAmount) || 0 
      }));
    } else {
      const totalMultiplier = includedPlayers.reduce((sum, p) => sum + p.multiplier, 0);
      if (totalMultiplier === 0) return includedPlayers.map(p => ({ ...p, charge: 0 }));
      
      return includedPlayers.map(p => ({
        ...p,
        charge: (netAmount / totalMultiplier) * p.multiplier
      }));
    }
  };

  const handlePreviewCharges = () => {
    if (!totalBill || parseFloat(totalBill) <= 0) {
      toast.error("Please enter a valid total bill amount");
      return;
    }

    const includedPlayers = players.filter(p => p.included);
    if (includedPlayers.length === 0) {
      toast.error("Please include at least one player");
      return;
    }

    if (chargeMethod === "fixed") {
      const invalidPlayers = includedPlayers.filter(p => !p.fixedAmount || parseFloat(p.fixedAmount) <= 0);
      if (invalidPlayers.length > 0) {
        toast.error(`Please enter valid amounts for all included players`);
        return;
      }
    }

    const charges = calculatePreview();
    setCalculatedCharges(charges);
    setShowPreview(true);
  };

  const handleGenerateOrEdit = async () => {
    setGenerating(true);
    try {
      const payload = {
        total_bill: parseFloat(totalBill),
        receipt_number: receiptNumber || null,
        has_subsidy: hasSubsidy,
        manual_subsidy_amount: manualSubsidyMode ? parseFloat(manualSubsidyAmount) || null : null,
        charge_method: chargeMethod,
        fixed_amount: chargeMethod === "fixed" ? parseFloat(fixedAmount) : null,
        remarks_to_finance: remarksToFinance || null,
        players: players.map(p => ({
          user_id: p.user_id,
          included: p.included,
          multiplier: p.multiplier,
          fixed_amount: chargeMethod === "fixed" ? (parseFloat(p.fixedAmount) || 0) : null
        }))
      };

      let response;
      if (isEditMode && existingSheet) {
        // Edit existing sheet
        response = await axios.put(
          `${API}/charge-sheets/${existingSheet.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success(`Charge sheet updated! Ref: ${response.data.reference_number}`);
      } else {
        // Create new sheet
        response = await axios.post(
          `${API}/games/${game.id}/charge-sheet`,
          payload,
          { withCredentials: true }
        );
        toast.success(`Charge sheet created! Ref: ${response.data.reference_number}`);
      }

      setShowPreview(false);
      
      // Reload to get latest data
      await checkExistingSheet();
    } catch (error) {
      console.error("Error with charge sheet:", error);
      toast.error(error.response?.data?.detail || "Failed to process charge sheet");
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/games/${game.id}/charge-sheet/download`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ChargeSheet_${existingSheet?.reference_number || game.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Charge sheet downloaded!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto"></div>
        <p className="text-center mt-4 text-gray-600">Loading charge sheet...</p>
      </div>
    );
  }

  // Preview Dialog
  if (showPreview) {
    const totalCharges = calculatedCharges.reduce((sum, p) => sum + p.charge, 0);

    return (
      <div className="p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h2 className="text-xl font-bold">Review Charges Before {isEditMode ? 'Updating' : 'Generating'}</h2>
        </div>

        <Card className="p-4 mb-4 bg-yellow-500/10">
          <h3 className="font-semibold mb-2">Summary</h3>
          <div className="text-sm space-y-1">
            <p>Total Bill: <strong>${parseFloat(totalBill).toFixed(2)}</strong></p>
            {receiptNumber && <p>Receipt/Bill #: <strong>{receiptNumber}</strong></p>}
            <p>Subsidy: <strong>${subsidyAmount.toFixed(2)}</strong></p>
            <p>Net Amount: <strong>${(parseFloat(totalBill) - subsidyAmount).toFixed(2)}</strong></p>
            <p>Method: <strong>{chargeMethod === "fixed" ? "Fixed Amount" : "Multiplier Based"}</strong></p>
          </div>
        </Card>

        {/* Warning Banner for Missing Membership Numbers */}
        {calculatedCharges.some(p => !p.membership_number || p.membership_number === "N/A") && (
          <Card className="p-4 mb-4 bg-red-500/10 border-2 border-red-500/30">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-red-400">Missing Membership Numbers</h4>
                <p className="text-sm text-red-600 mt-1">
                  The following players don't have membership numbers on file. 
                  Finance may not be able to process charges for them:
                </p>
                <ul className="mt-2 text-sm text-red-400 list-disc list-inside">
                  {calculatedCharges
                    .filter(p => !p.membership_number || p.membership_number === "N/A")
                    .map((p, idx) => (
                      <li key={idx}>{p.name}</li>
                    ))
                  }
                </ul>
                <p className="text-xs text-red-500 mt-2">
                  Please ask these players to update their profile with their SCC membership number.
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="space-y-2 mb-4">
          {calculatedCharges.map((player, idx) => (
            <Card key={idx} className={`p-4 flex items-center justify-between ${(!player.membership_number || player.membership_number === "N/A") ? "border-red-500/30 bg-red-500/10" : ""}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{player.name}</p>
                  {(!player.membership_number || player.membership_number === "N/A") && (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <p className={`text-sm ${(!player.membership_number || player.membership_number === "N/A") ? "text-red-500 font-medium" : "text-gray-600"}`}>
                  {player.membership_number || "N/A"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">x{player.multiplier}</p>
                <p className="text-lg font-bold text-green-600">${player.charge.toFixed(2)}</p>
              </div>
            </Card>
          ))}
        </div>

        <Card className="p-4 mb-4 bg-green-500/10">
          <p className="text-lg font-bold">Total Charges: ${totalCharges.toFixed(2)}</p>
          <p className="text-sm text-gray-600">{calculatedCharges.length} player(s)</p>
        </Card>

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowPreview(false)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleGenerateOrEdit}
            disabled={generating}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {generating ? "Processing..." : (isEditMode ? "Confirm Update" : "Confirm & Generate")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">
            {isEditMode ? (
              <span className="flex items-center gap-2">
                <Edit className="w-5 h-5 sm:w-6 sm:h-6" />
                Edit Charge Sheet
              </span>
            ) : "Generate Charge Sheet"}
          </h2>
          {existingSheet && (
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Current: <strong>{existingSheet.reference_number}</strong> (v{existingSheet.version})
            </p>
          )}
        </div>
        {existingSheet && (
          <Button onClick={downloadPDF} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-1" />
            PDF
          </Button>
        )}
      </div>

      {/* Bill Details */}
      <Card className="p-4 mb-4">
        <h3 className="font-semibold mb-3">Bill Details</h3>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Total Bill ($)</label>
            <Input
              type="number"
              value={totalBill}
              onChange={(e) => setTotalBill(e.target.value)}
              placeholder="150.00"
              step="0.01"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Bar Bill / Receipt Number</label>
            <Input
              type="text"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              placeholder="e.g., BB-12345 or Receipt #67890"
              data-testid="receipt-number-input"
            />
            <p className="text-xs text-gray-500 mt-1">Enter the bar bill or dinner receipt reference number</p>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={hasSubsidy}
              onCheckedChange={(checked) => {
                setHasSubsidy(checked);
                if (!checked) {
                  setManualSubsidyMode(false);
                  setManualSubsidyAmount("");
                }
              }}
            />
            <label className="text-sm">
              Apply Club Subsidy
            </label>
          </div>

          {hasSubsidy && (
            <div className="bg-green-500/10 border border-green-500/30 p-3 rounded space-y-3">
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="subsidyMode"
                    checked={!manualSubsidyMode}
                    onChange={() => {
                      setManualSubsidyMode(false);
                      setManualSubsidyAmount("");
                    }}
                    className="accent-green-500"
                  />
                  <span className="text-sm">Auto (50% up to $100, min $75)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="subsidyMode"
                    checked={manualSubsidyMode}
                    onChange={() => setManualSubsidyMode(true)}
                    className="accent-green-500"
                  />
                  <span className="text-sm">Manual Amount</span>
                </label>
              </div>
              
              {manualSubsidyMode && (
                <div>
                  <label className="text-xs font-medium text-slate-400">Manual Subsidy Amount ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={manualSubsidyAmount}
                    onChange={(e) => setManualSubsidyAmount(e.target.value)}
                    placeholder="Enter subsidy amount"
                    className="mt-1 bg-slate-800 border-slate-600"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Enter any subsidy amount (overrides auto-calculation)
                  </p>
                </div>
              )}
              
              <div className="text-sm font-medium text-green-400">
                Subsidy Amount: <strong>${subsidyAmount.toFixed(2)}</strong>
                {!manualSubsidyMode && parseFloat(totalBill) < 75 && parseFloat(totalBill) > 0 && (
                  <span className="text-xs text-yellow-400 ml-2">(Min $75 spend required for auto subsidy)</span>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="text-sm font-medium">Charge Method</label>
            <div className="flex gap-2 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="chargeMethod"
                  value="multiplier"
                  checked={chargeMethod === "multiplier"}
                  onChange={(e) => setChargeMethod(e.target.value)}
                />
                <span className="text-sm">Multiplier Based</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="chargeMethod"
                  value="fixed"
                  checked={chargeMethod === "fixed"}
                  onChange={(e) => setChargeMethod(e.target.value)}
                />
                <span className="text-sm">Fixed Amount</span>
              </label>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Remarks to Finance (Optional)</label>
            <textarea
              className="w-full mt-2 p-2 border rounded text-sm"
              rows={3}
              placeholder="Add any notes for the finance team..."
              value={remarksToFinance}
              onChange={(e) => setRemarksToFinance(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">This will be included in the email to charges@sccfootball.com</p>
          </div>
        </div>
      </Card>

      {/* Players */}
      <Card className="p-4 mb-4">
        <h3 className="font-semibold mb-3">Players ({players.filter(p => p.included).length} included)</h3>
        <div className="space-y-2">
          {players.map((player, index) => (
            <div key={index} className="flex items-center gap-2 sm:gap-3 p-3 border rounded">
              <Checkbox
                checked={player.included}
                onCheckedChange={() => togglePlayerIncluded(index)}
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{player.name}</p>
                <div className="flex items-center gap-2">
                  {player.membership_number && player.membership_number !== "N/A" ? (
                    <p className="text-xs text-green-600 font-medium">{player.membership_number}</p>
                  ) : (
                    <>
                      <p className="text-xs text-red-500">No membership #</p>
                      {player.member_type === "academy_parent" && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">Academy Parent</span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1 text-xs text-blue-600 hover:text-blue-700"
                        onClick={() => openLinkDialog(player, index)}
                        data-testid={`link-member-btn-${index}`}
                      >
                        <Link2 className="h-3 w-3 mr-1" />
                        Link
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                {chargeMethod === "multiplier" ? (
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updatePlayerMultiplier(index, Math.max(1, player.multiplier - 1))}
                      disabled={!player.included || player.multiplier <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-medium">{player.multiplier}x</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updatePlayerMultiplier(index, player.multiplier + 1)}
                      disabled={!player.included}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <label className="text-sm">$</label>
                    <Input
                      type="number"
                      value={player.fixedAmount}
                      onChange={(e) => updatePlayerFixedAmount(index, e.target.value)}
                      className="w-20"
                      placeholder="10.00"
                      step="0.01"
                      disabled={!player.included}
                    />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Link Member Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link Member to Membership Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-2">Player Name:</p>
              <p className="font-medium">{linkingPlayer?.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium">Membership Number</label>
              <Input
                type="text"
                value={linkMembershipNumber}
                onChange={(e) => setLinkMembershipNumber(e.target.value.toUpperCase())}
                placeholder="e.g., P1234, C5678HFM, V0271"
                className="mt-1"
                data-testid="link-membership-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the exact membership number from the member's profile
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setLinkDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLinkMember}
                disabled={linkingInProgress || !linkMembershipNumber.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-link-btn"
              >
                {linkingInProgress ? "Linking..." : "Link Member"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button 
          onClick={handlePreviewCharges}
          className="flex-1 bg-yellow-600 hover:bg-yellow-700"
        >
          <Eye className="w-4 h-4 mr-2" />
          Preview Charges
        </Button>
      </div>
    </div>
  );
}
